import { motion } from 'framer-motion';
import { ArrowRight, Users, Target, Trophy, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } },
};

const steps = [
  { icon: Users, title: '1. צרו קבוצה', desc: 'הזמינו חברים עם קוד הצטרפות ייחודי והתחרו ביניכם' },
  { icon: Target, title: '2. נחשו תוצאות סדרות', desc: 'לפני כל סדרה - בחרו מנצחת ותוצאה מדויקת' },
  { icon: Zap, title: '3. הימורי צד', desc: 'בכל סיבוב - ניחושים נוספים על סטטיסטיקות שלא קשורים למנצחת. מי יוביל בנקודות? כמה שלשות ייקלעו?' },
  { icon: Trophy, title: '4. צברו נקודות ונצחו!', desc: 'טפסו בטבלה והוכיחו שאתם הפרשנים הכי טובים' },
];

const bonusRows = [
  { color: '#22c55e', label: 'קל - פייבוריט ברור', points: '+2' },
  { color: '#3b82f6', label: 'סביר - פייבוריט קל', points: '+3' },
  { color: '#eab308', label: 'מאוזן - יכול ללכת לכל כיוון', points: '+5' },
  { color: '#f97316', label: 'קשה - אנדרדוג', points: '+7' },
  { color: '#ef4444', label: 'מאתגר - נגד הסיכויים', points: '+9' },
  { color: '#a855f7', label: 'על הקצה - לונגשוט', points: '+12' },
];

const HowItWorks = () => (
  <Layout>
    <div className="font-leon min-h-screen bg-[#15191E] text-[#FEFEFE]" dir="rtl">
      <div className="container mx-auto px-4 py-10 max-w-2xl">
        <Link to="/" className="text-sm text-white/60 hover:text-[#F97116] transition-colors flex items-center gap-1 mb-6 min-h-[44px]">
          <ArrowRight className="w-4 h-4 rotate-180" /> חזרה
        </Link>

        <motion.h1
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="text-3xl font-bold tracking-tight mb-8"
        >
          איך <span>BRACKET</span> <span className="text-gradient">RUN</span> עובד?
        </motion.h1>

        {/* מה זה? */}
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-white/70 leading-relaxed mb-8"
        >
          משחק ניחושים חברתי לפלייאוף ה-NBA. בחרו מנצחת ותוצאה מדויקת לכל סדרה, הימרו על סטטיסטיקות, וצברו נקודות. ככל שהניחוש מפתיע יותר - כך הבונוס גבוה יותר.
        </motion.p>

        {/* Steps */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="space-y-4 mb-10"
        >
          {steps.map((step, i) => {
            const isGold = i === 2;
            return (
              <motion.div
                key={i}
                variants={fadeUp}
                className="bg-[#1A1F25] border border-white/5 rounded-2xl p-6 flex items-start gap-5"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={isGold
                    ? { backgroundColor: 'rgba(196,164,103,0.1)', border: '1px solid rgba(196,164,103,0.3)' }
                    : { backgroundColor: 'rgba(249,113,22,0.1)', border: '1px solid rgba(249,113,22,0.3)' }
                  }
                >
                  <step.icon
                    className="w-6 h-6"
                    style={isGold ? { color: '#C4A467' } : { color: '#F97116' }}
                  />
                </div>
                <div>
                  <h3 className="font-bold mb-1">{step.title}</h3>
                  <p className="text-sm text-white/70">{step.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* איך צוברים נקודות? */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-10"
        >
          <p className="section-label mb-4">איך צוברים נקודות?</p>
          <div className="flex gap-4">
            <div className="bg-[#1A1F25] border border-white/5 rounded-xl p-4 text-center flex-1">
              <p className="text-sm text-white/70">מנצחת נכונה</p>
              <p className="text-green-400 text-2xl font-bold">+2</p>
              <p className="text-xs text-white/50">תמיד</p>
            </div>
            <div className="bg-[#1A1F25] border border-white/5 rounded-xl p-4 text-center flex-1">
              <p className="text-sm text-white/70">תוצאה מדויקת</p>
              <p className="text-[#F97116] text-2xl font-bold">+2 עד +12</p>
              <p className="text-xs text-white/50">לפי רמת הקושי</p>
            </div>
          </div>
        </motion.div>

        {/* טבלת בונוס */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-10"
        >
          <p className="section-label mb-4">טבלת בונוס</p>
          <div className="bg-[#1A1F25] border border-white/5 rounded-2xl overflow-hidden">
            {bonusRows.map((row, i) => (
              <div
                key={i}
                className={`flex items-center justify-between px-4 py-3${i < bonusRows.length - 1 ? ' border-b border-white/5' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full inline-block shrink-0" style={{ backgroundColor: row.color }} />
                  <span className="text-sm">{row.label}</span>
                </div>
                <span className="font-bold text-[#F97116]">{row.points}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* דוגמה */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-10"
        >
          <p className="section-label mb-3">דוגמה</p>
          <p className="text-sm text-white/70 mb-4">ניחשת: Thunder 4-2 (קשה - אנדרדוג)</p>
          <div className="flex items-center gap-2 justify-center">
            <div className="bg-[#1A1F25] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-xs text-white/70">מנצחת</p>
              <p className="text-lg font-bold text-green-400">+2</p>
            </div>
            <span className="text-white/50 text-lg">+</span>
            <div className="bg-[#1A1F25] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-xs text-white/70">תוצאה מדויקת</p>
              <p className="text-lg font-bold text-[#F97116]">+7</p>
            </div>
            <span className="text-white/50 text-lg">=</span>
            <div className="bg-[#1A1F25] border border-[#F97116]/40 rounded-xl p-3 text-center">
              <p className="text-xs text-white/70">סה"כ</p>
              <p className="text-xl font-bold text-[#F97116]">9</p>
            </div>
          </div>
        </motion.div>

        {/* בונוס: ברקט גלובלי */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-10"
        >
          <p className="font-bold text-lg" style={{ color: '#DAA520' }}>🏆 בונוס: ברקט גלובלי</p>
          <div className="p-5 mt-3" style={{ background: '#141414', border: '1.5px solid #DAA520', borderRadius: '12px' }}>
            <p className="text-sm text-white/70">
              בתחילת הפלייאוף - מלאו ברקט שלם פעם אחת: מנצחת ותוצאה לכל 15 הסדרות, מהסיבוב הראשון ועד הגמר. התחרו מול כל משתמשי האפליקציה בטבלה גלובלית.
            </p>
            <div className="rounded-xl p-4 mt-4 text-center" style={{ background: 'rgba(218,165,32,0.08)' }}>
              <p className="text-sm text-white/70">ניחוש אלוף ה-NBA נכון</p>
              <p className="text-3xl font-bold" style={{ color: '#DAA520' }}>+5</p>
              <p className="text-xs text-white/70">נקודות בונוס לקבוצה הפרטית</p>
            </div>
          </div>
        </motion.div>

        {/* Beta banner */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-white/5 border border-white/5 rounded-2xl p-6 text-center mb-6"
        >
          <p className="text-sm text-white/70">
            זו גרסה ראשונה - נשמח לכל פידבק, רעיון, או דיווח על באג. שלחו לנו ונשפר ביחד!
          </p>
        </motion.div>
      </div>
    </div>
  </Layout>
);

export default HowItWorks;
